
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartContainer, ChartTooltipContent } from '@/components/ui/chart';
import { useEffect, useState } from 'react';

interface FinancialAnalyticsChartProps {
  storeId: string;
  data: { date: string; revenue: number }[];
}

export const FinancialAnalyticsChart = ({ storeId, data }: FinancialAnalyticsChartProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [formattedData, setFormattedData] = useState([]);

  useEffect(() => {
    if (data && data.length > 0) {
      // Format the data for the chart
      const formatted = data.map(item => ({
        name: format(new Date(item.date), 'MMM'),
        revenue: item.revenue,
      }));
      
      setFormattedData(formatted);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [data]);

  return (
    <Card className="col-span-full animate-fade-in">
      <CardHeader>
        <CardTitle>{t('dashboardCharts.monthlyRevenue')}</CardTitle>
        <CardDescription>
          {t('dashboardCharts.monthlyRevenueDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="h-80">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <p>{t('loading')}</p>
          </div>
        ) : (
          <ChartContainer 
            config={{
              revenue: {
                label: t('dashboardCharts.revenue'),
                theme: {
                  light: '#7E69AB',
                  dark: '#9b87f5',
                },
              }
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={formattedData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor="var(--color-revenue, hsl(var(--primary)))"
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--color-revenue, hsl(var(--primary)))"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" />
                <YAxis />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip content={<ChartTooltipContent labelKey="name" />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--color-revenue, hsl(var(--primary)))"
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
